import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';

const News = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('news');
  const [loading, setLoading] = useState(true);
  const [newsArticles, setNewsArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [userMembership, setUserMembership] = useState(null);
  const [userRsvps, setUserRsvps] = useState({});
  const [rsvpLoading, setRsvpLoading] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    fetchUserMembership();
  }, [user]);

  useEffect(() => {
    if (userMembership !== null || activeTab === 'news') {
      fetchData();
    }
  }, [userMembership]);

  useEffect(() => {
    if (user && activeTab === 'events') {
      fetchUserRsvps();
    }
  }, [user, activeTab, events]);

  const fetchUserMembership = async () => {
    if (!user) {
      setUserMembership('Guest');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('membership_tier')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      setUserMembership(data?.membership_tier || 'Guest');
    } catch (err) {
      console.error('Error fetching user membership:', err);
      setUserMembership('Guest');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch published news
      const { data: newsData, error: newsError } = await supabase
        .from('news')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      
      if (newsError) throw newsError;

      // Fetch published events
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('is_published', true)
        .order('event_date', { ascending: false });
      
      if (eventsError) throw eventsError;

      // Format data for display
      const formattedNews = (newsData || []).map(item => ({
        ...item,
        date: new Date(item.published_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        image: item.image_url
      }));

      // Filter events by user's membership tier
      const filteredEventsData = (eventsData || []).filter(event => {
        if (!event.target_tiers || event.target_tiers.length === 0) {
          return true; // Show events with no tier restrictions to everyone
        }
        return event.target_tiers.includes(userMembership);
      });

      const formattedEvents = filteredEventsData.map(item => {
        // Format event_time from 24h (HH:mm:ss) to 12h format
        let formattedTime = item.event_time;
        if (item.event_time) {
          const [hours, minutes] = item.event_time.split(':');
          const hour = parseInt(hours, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour % 12 || 12;
          formattedTime = `${displayHour}:${minutes} ${ampm}`;
        }
        
        return {
          ...item,
          date: new Date(item.event_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          event_time: formattedTime,
          image: item.image_url
        };
      });

      setNewsArticles(formattedNews);
      setEvents(formattedEvents);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRsvps = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('event_id, rsvp_status')
        .eq('user_id', user.id)
        .eq('rsvp_status', 'confirmed');
      
      if (error) throw error;

      const rsvpMap = {};
      (data || []).forEach(rsvp => {
        rsvpMap[rsvp.event_id] = true;
      });
      setUserRsvps(rsvpMap);
    } catch (err) {
      console.error('Error fetching RSVPs:', err);
    }
  };

  const handleRSVP = async (event) => {
    if (!user) {
      alert('Please log in to RSVP for events');
      return;
    }

    // Check if RSVP deadline has passed
    if (event.rsvp_deadline && new Date(event.rsvp_deadline) < new Date()) {
      alert('RSVP deadline has passed for this event');
      return;
    }

    setRsvpLoading(prev => ({ ...prev, [event.id]: true }));

    try {
      // Get user details
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('full_name, email, membership_tier')
        .eq('id', user.id)
        .single();
      
      if (userError) throw userError;

      // Check if already RSVP'd
      const { data: existingRsvp, error: checkError } = await supabase
        .from('event_rsvps')
        .select('id, rsvp_status')
        .eq('event_id', event.id)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      if (existingRsvp) {
        // Cancel RSVP
        const { error: deleteError } = await supabase
          .from('event_rsvps')
          .delete()
          .eq('id', existingRsvp.id);
        
        if (deleteError) throw deleteError;

        setUserRsvps(prev => {
          const newRsvps = { ...prev };
          delete newRsvps[event.id];
          return newRsvps;
        });

        alert('RSVP cancelled successfully!');
      } else {
        // Create RSVP
        const { error: insertError } = await supabase
          .from('event_rsvps')
          .insert({
            event_id: event.id,
            user_id: user.id,
            user_name: userData.full_name,
            user_email: userData.email,
            membership_tier: userData.membership_tier,
            rsvp_status: 'confirmed'
          });
        
        if (insertError) throw insertError;

        setUserRsvps(prev => ({ ...prev, [event.id]: true }));
        alert('RSVP confirmed! See you at the event.');
      }
    } catch (err) {
      console.error('Error handling RSVP:', err);
      alert('Failed to process RSVP. Please try again.');
    } finally {
      setRsvpLoading(prev => ({ ...prev, [event.id]: false }));
    }
  };

  const filteredNews = newsArticles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayItems = activeTab === 'news' ? filteredNews : filteredEvents;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <h1 className="page-title">News & Events</h1>
        <div className="underline"></div>
        <p className="page-subtitle">Stay Updated with IBV Wealth Club</p>
        
        <SearchBar 
          placeholder="Search news or events..."
          onSearch={setSearchTerm}
        />

        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          marginTop: '20px'
        }}>
          <button
            onClick={() => setActiveTab('news')}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'news' ? '#CDB688' : '#ffffff',
              color: activeTab === 'news' ? '#ffffff' : '#1a1a1a',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'news' ? '0 2px 8px rgba(205, 182, 136, 0.3)' : 'none'
            }}
          >
            <i className="fas fa-newspaper" style={{marginRight: '8px'}}></i>
            News
          </button>
          <button
            onClick={() => setActiveTab('events')}
            style={{
              flex: 1,
              padding: '12px',
              background: activeTab === 'events' ? '#CDB688' : '#ffffff',
              color: activeTab === 'events' ? '#ffffff' : '#1a1a1a',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'events' ? '0 2px 8px rgba(205, 182, 136, 0.3)' : 'none'
            }}
          >
            <i className="fas fa-calendar-alt" style={{marginRight: '8px'}}></i>
            Events
          </button>
        </div>

        {displayItems.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#ffffff',
            borderRadius: '15px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <i className={`fas fa-${activeTab === 'news' ? 'newspaper' : 'calendar'}`} 
              style={{ fontSize: '48px', color: '#ddd', marginBottom: '16px' }}></i>
            <p style={{ color: '#666', fontSize: '16px' }}>
              No {activeTab} available yet. Check back soon!
            </p>
          </div>
        ) : (
          <div className="blog-grid">
            {displayItems.map((item, index) => (
              <div 
                key={item.id} 
                className={`blog-card ${index === 0 ? 'featured-post' : ''}`}
              >
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="blog-image"
                />
                <div className="blog-content">
                  <div className="blog-meta">
                    <span className="blog-tag">{item.category}</span>
                    <span>
                      <i className="far fa-calendar"></i> {item.date}
                    </span>
                    {activeTab === 'events' && item.event_time && (
                      <span>
                        <i className="far fa-clock"></i> {item.event_time}
                      </span>
                    )}
                    {activeTab === 'events' && item.location && (
                      <span>
                        <i className="fas fa-map-marker-alt"></i> {item.location}
                      </span>
                    )}
                  </div>
                  <h3 className="blog-title">{item.title}</h3>
                  <p className="blog-excerpt">{item.excerpt}</p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                      <Link to={`/blog/${item.slug}`} className="blog-read-more" style={{ flex: 1 }}>
                        Read More <i className="fas fa-arrow-right"></i>
                      </Link>
                      {activeTab === 'events' && (() => {
                        const rsvpDeadlinePassed = item.rsvp_deadline && new Date(item.rsvp_deadline) < new Date();
                        return (
                          <button
                            onClick={() => handleRSVP(item)}
                            disabled={rsvpLoading[item.id] || rsvpDeadlinePassed}
                            style={{
                              padding: '10px 20px',
                              background: rsvpDeadlinePassed ? '#9e9e9e' : (userRsvps[item.id] ? '#4caf50' : '#CDB688'),
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: (rsvpLoading[item.id] || rsvpDeadlinePassed) ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease',
                              opacity: (rsvpLoading[item.id] || rsvpDeadlinePassed) ? 0.6 : 1,
                              whiteSpace: 'nowrap'
                            }}
                            onMouseOver={(e) => {
                              if (!rsvpLoading[item.id] && !rsvpDeadlinePassed) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {rsvpLoading[item.id] ? (
                              <>
                                <i className="fas fa-spinner fa-spin" style={{marginRight: '6px'}}></i>
                                Processing...
                              </>
                            ) : rsvpDeadlinePassed ? (
                              <>
                                <i className="fas fa-times-circle" style={{marginRight: '6px'}}></i>
                                Closed
                              </>
                            ) : userRsvps[item.id] ? (
                              <>
                                <i className="fas fa-check-circle" style={{marginRight: '6px'}}></i>
                                RSVP'd
                              </>
                            ) : (
                              <>
                                <i className="fas fa-calendar-check" style={{marginRight: '6px'}}></i>
                                RSVP
                              </>
                            )}
                          </button>
                        );
                      })()}
                    </div>
                    {activeTab === 'events' && item.rsvp_deadline && (
                      <small style={{ 
                        fontSize: '11px', 
                        color: new Date(item.rsvp_deadline) < new Date() ? '#f44336' : '#666',
                        fontWeight: '600',
                        width: '100%',
                        textAlign: 'center'
                      }}>
                        <i className="fas fa-clock" style={{marginRight: '4px'}}></i>
                        RSVP by: {new Date(item.rsvp_deadline).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {new Date(item.rsvp_deadline) < new Date() && ' (Closed)'}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default News;
